import { routerService } from "cruzo";
import type { Step } from "site/services/app.service";
import { routerUrlBucket } from "site/urls";

export function zeptoRowNumberFromId(zepto: unknown[], rowId: number): number | null {
  for (let index = 0; index < zepto.length; index += 1) {
    const row = zepto[index];
    if (!Array.isArray(row) || row.length < 3) {
      continue;
    }
    const id = Number(row[2]);
    if (Number.isFinite(id) && id === rowId) {
      return index + 1;
    }
  }
  return null;
}

function toZeptoRowId(value: unknown): number | null {
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
}

export function resolveStepItemZeptoRowId(
  step: Step | null,
  itemIndexOneBased: number,
): number | null {
  if (!step?.data) {
    return null;
  }

  const chunk = step.data[step.subIndex];
  const itemIndex = Math.max(0, (itemIndexOneBased || 1) - 1);
  const key = step.descriptor.key;

  if (key === "single" || key === "select" || key === "sentences" || key === "rules") {
    return toZeptoRowId(chunk?.id);
  }

  if (Array.isArray(chunk) && chunk.length) {
    const item = chunk[itemIndex] ?? chunk[0];
    return toZeptoRowId(item?.id);
  }

  return null;
}

export function buildExerciseEditorRowHref(
  bookId: string,
  exerciseId: string,
  rowNumber: number,
): string {
  const query = new URLSearchParams();
  query.set("row", String(rowNumber));
  return routerUrlBucket.buildUrl("exercise_edit", { bookId, exerciseId }, query);
}

export function buildExerciseEditorRowIdHref(
  bookId: string,
  exerciseId: string,
  rowId: number,
): string {
  const query = new URLSearchParams();
  query.set("rowId", String(rowId));
  return routerUrlBucket.buildUrl("exercise_edit", { bookId, exerciseId }, query);
}

export function readExerciseEditorRowJumpParam(): number | null {
  const raw = new URLSearchParams(routerService.search$.actual).get("row");
  if (!raw) {
    return null;
  }
  const num = Number(raw);
  if (!Number.isFinite(num)) {
    return null;
  }
  return Math.max(1, Math.floor(num));
}

export function readExerciseEditorRowIdJumpParam(): number | null {
  const raw = new URLSearchParams(routerService.search$.actual).get("rowId");
  if (!raw) {
    return null;
  }
  const num = Number(raw);
  if (!Number.isFinite(num)) {
    return null;
  }
  return Math.floor(num);
}

function replaceHistoryHref(href: string): void {
  if (routerService.isHashMode()) {
    const hash = href.startsWith("#/") ? href : `#/${String(href).replace(/^\//, "")}`;
    const next = `${window.location.pathname}${window.location.search}${hash}`;
    if (`${window.location.pathname}${window.location.search}${window.location.hash}` === next) {
      return;
    }
    history.replaceState(null, "", next);
    routerService.update();
    return;
  }

  const url = new URL(href, window.location.origin);
  const path = url.pathname.replace(/\/+$/, "") || url.pathname;
  const next = `${path}${url.search}${url.hash}`;
  if (`${window.location.pathname}${window.location.search}${window.location.hash}` === next) {
    return;
  }
  history.replaceState("", "", next);
  routerService.update();
}

/** Updates ?row= in the editor URL without adding a history entry. */
export function syncExerciseEditorRowHref(
  bookId: string,
  exerciseId: string,
  rowNumber: number,
): void {
  replaceHistoryHref(buildExerciseEditorRowHref(bookId, exerciseId, rowNumber));
}

/** @deprecated use syncExerciseEditorRowHref */
export function pushExerciseEditorRowHref(
  bookId: string,
  exerciseId: string,
  rowNumber: number,
) {
  syncExerciseEditorRowHref(bookId, exerciseId, rowNumber);
}
